import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { config } from 'elc-service';
import { ISliderSettings } from 'elc-slider';
import { IconTypes } from 'elc-accordion';
import { ContentSpacing, Breakpoints } from 'elc-base-theme';
import { IItem, IParamsById } from '../../api/ProdcatApiSdk';
import { DEFAULT_GRID_BREAKPOINTS, contentSize } from '../../internal/constants/ProductGrid';
import { IProductCarousel } from '../../internal/views/product-carousel/ProductCarousel';
import { ProductGridViewController } from '../../internal/controllers/ProductGridViewController';
import {
    IBreakpoints,
    IProductBriefCollection
} from '../../internal/views/product-brief-collection/ProductBriefCollection';
import { CartLimitReachedModal } from '../../internal/views/common/CartLimitReachedModal';
import { IFiltersTranslation } from '../../internal/views/product-filters/DesktopFilters';
import ProductGrid, { Formatter } from '../../internal/views/product-grid/ProductGrid';
import { ProductAccordion } from '../../internal/views/product-accordion/ProductAccordion';
import { mergeConfigs } from '../../internal/utils/ConfigUtils';
import {
    IProdcatConfig,
    ISizePickerImages,
    IInventoryStatusSorting,
    IProductBenefitTypes
} from '../../internal/interfaces/IProdcat';
import { TOGGLE_TYPE } from '../../internal/constants/AutoReplenish';
import { IFilterData } from '../../internal/views/product-filters/IProductFilters';
import { Container, Column } from 'elc-grid';
import { CategoryHeader } from '../../internal/views/product-grid/CategoryHeader';
import { ProductGridHeaders } from '../../internal/views/product-grid/ProductGridHeaders';

export interface IContentItem {
    nId: number | string;
    nPsn: number;
    nSize: contentSize;
}

export interface ITranslationsOverwrites {
    quickViewButtonLabel?: string;
    ctaButtonLabel?: string;
    pressedCtaButtonLabel?: string;
}

export interface IProductImages {
    product_grid?: {
        lazyload?: boolean;
    };
}

export interface IProductGridConfig extends IProdcatConfig {
    items?: IItem[];
    paramsById?: IParamsById;
    featuredProductId?: string;
    viewableInventoryStatuses?: string[];
    productTags?: string[];
    content?: IContentItem[];
    productGridBreakpoints: IBreakpoints;
    enableGridCarousel?: boolean;
    carouselSettings?: ISliderSettings;
    showSubDisplayName?: boolean;
    toutWidth?: number;
    toutHeight?: number;
    ctaType?: string;
    enableNotifyMe?: boolean;
    enableQuickView?: boolean;
    hideSorting?: boolean;
    hideReviewsNumber?: boolean;
    disableFeaturedProduct?: boolean;
    filterData?: IFilterData;
    enableFilters?: boolean;
    enableSizePicker?: boolean;
    featuredProductOverrideImage?: string;
    autoReplenishOptions?: number[];
    autoReplenishToggleType?: TOGGLE_TYPE;
    gridAccordionIconType?: IconTypes;
    enableAccordion?: boolean;
    accordionDefaultOpen?: boolean;
    accordionTitle?: string;
    accordionSubtext?: string;
    enableMobileQuickView?: boolean;
    productGridHeader?: string;
    productGridSubheader?: string;
    sizePickerImages?: ISizePickerImages;
    enableCategoryHeader?: boolean;
    showDynamicCategoryHeader?: boolean;
    hideEmptyStars?: boolean;
    translationsOverwrites?: ITranslationsOverwrites;
    gridCTA?: ICTAConfig;
    productImages?: IProductImages;
    lazyloadProductGridItems?: boolean;
    inventoryStatusSorting?: IInventoryStatusSorting;
    productBenefitTypes?: IProductBenefitTypes;
}

export interface IProductGridWrapper {
    config: IProductGridConfig;
    className?: string;
    translations?: IFiltersTranslation;
    viewController: ProductGridViewController;
}

export interface ICTAConfig {
    enabled?: boolean;
    label?: string;
    absoluteUrl?: boolean;
    url?: string;
    newTab?: boolean;
}

const StyledContainer = styled(Container)`
    margin-top: ${ContentSpacing.space20};
`;

export const StyledHeaderColumn = styled(Column)`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

@config(['prodcatConfig'], mergeConfigs)
@observer
class ProductGridWrapper extends React.Component<IProductGridWrapper> {
    public static displayName: string;

    constructor(props: IProductGridWrapper) {
        super(props);

        props.viewController.handleState(props.config);
    }

    public async componentDidMount() {
        await this.props.viewController.loaded(this.props);
    }

    private getBriefProps = () => {
        const {
            data: { isLoaded, gridItems, featuredProductId }
        } = this.props.viewController;
        const { config: propsConfig } = this.props;

        const briefProps: IProductBriefCollection['config'] = {
            gridItems,
            isLoaded,
            featuredProductId,
            defaultBreakpoints: DEFAULT_GRID_BREAKPOINTS,
            breakpoints: propsConfig.productGridBreakpoints,
            hideProductBadge: propsConfig.hideProductBadge,
            showSubDisplayName: propsConfig.showSubDisplayName,
            enableGridCarousel: propsConfig.enableGridCarousel,
            hideProductPrice: propsConfig.hideProductPrice,
            hideProductPriceRange: propsConfig.hideProductPriceRange,
            hideProductRating: propsConfig.hideProductRating,
            ctaType: propsConfig.ctaType,
            enableNotifyMe: propsConfig.enableNotifyMe,
            enableQuickView: propsConfig.enableQuickView,
            enableMobileQuickView: propsConfig.enableMobileQuickView,
            hideReviewsNumber: propsConfig.hideReviewsNumber,
            productGridHideReadReviewsLink: propsConfig.productGridHideReadReviewsLink,
            enableHoverInteraction: propsConfig.enableHoverInteraction,
            enableShadePicker: propsConfig.enableShadePicker,
            enableSizePicker: propsConfig.productGridEnableSizePicker,
            shortDescriptionLocation: propsConfig.shortDescriptionLocation,
            enableProductDetailLink: propsConfig.enableProductDetailLink,
            enableQuickViewShortcut: propsConfig.enableQuickViewShortcut,
            featuredProductOverrideImage: propsConfig.featuredProductOverrideImage,
            enableLearnMoreLink: propsConfig.enableLearnMoreLink,
            autoReplenishOptions: propsConfig.autoReplenishOptions || [],
            autoReplenishToggleType: propsConfig.autoReplenishToggleType,
            enableNumberShades: propsConfig.enableNumberShades,
            sizePickerImages: propsConfig.sizePickerImages,
            hideProductPricePerUnit: propsConfig.hideProductPricePerUnit,
            showProductQuantitySelector: propsConfig.showProductQuantitySelector,
            hideEmptyStars: propsConfig.hideEmptyStars,
            enableAutoReplenishment: propsConfig.enableAutoReplenishment,
            translationsOverwrites: propsConfig.translationsOverwrites,
            productImages: propsConfig.productImages,
            lazyLoadGridItem: propsConfig.lazyloadProductGridItems,
            productBenefitTypes: propsConfig.productBenefitTypes
        };

        return briefProps;
    };

    private renderGridHeader = () => {
        const {
            productGridHeader,
            productGridSubheader,
            enableCategoryHeader,
            showDynamicCategoryHeader
        } = this.props.config;

        if (!enableCategoryHeader) {
            return null;
        }

        // If grid header or subheader were provided, use them to fill the header.
        if (productGridHeader || productGridSubheader) {
            return (
                <ProductGridHeaders
                    productGridHeader={productGridHeader}
                    productGridSubheader={productGridSubheader}
                />
            );
        }

        // Fall back to default category header if enabled
        const { categoryTag } = this.props.viewController.data;
        if (showDynamicCategoryHeader && categoryTag) {
            return <CategoryHeader categoryTag={categoryTag} />;
        }

        return null;
    };

    private setGridPosition = (openIndex: number) => {
        if (!this.props.config.accordionDefaultOpen && openIndex === 0) {
            this.props.viewController.setGridPosition();
        }
    };

    private renderGridCarousel = () => {
        const { config: propsConfig } = this.props;
        const { gridCTA } = propsConfig;
        const productGridCTAConfig = {
            enabled: gridCTA && gridCTA.enabled,
            label: gridCTA && gridCTA.label,
            absoluteUrl: gridCTA && gridCTA.absoluteUrl,
            url: gridCTA && gridCTA.url,
            newTab: gridCTA && gridCTA.newTab
        };
        const {
            numColumnsMobile,
            numColumnsDesktop,
            numColumnsLargeScreen,
            numColumnsExtraLargeScreen
        } = propsConfig.productGridBreakpoints;

        const carouselSettings = {
            dots: false,
            infinite: false,
            slidesToShow: numColumnsExtraLargeScreen,
            slidesToScroll: 1,
            responsive: [
                {
                    breakpoint: Breakpoints.extraLargeScreen,
                    settings: {
                        slidesToShow: numColumnsLargeScreen,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false
                    }
                },
                {
                    breakpoint: Breakpoints.largeScreen,
                    settings: {
                        slidesToShow: numColumnsDesktop,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false
                    }
                },
                {
                    breakpoint: Breakpoints.desktop,
                    settings: {
                        slidesToShow: numColumnsMobile,
                        slidesToScroll: 1,
                        infinite: false,
                        dots: false,
                        arrows: true
                    }
                }
            ]
        };

        const productCarouselProps: IProductCarousel['config'] = {
            carouselSettings,
            gridItems: this.props.viewController.data.gridItems,
            hideProductBadge: propsConfig.hideProductBadge,
            showSubDisplayName: propsConfig.showSubDisplayName,
            hideProductPrice: propsConfig.hideProductPrice,
            hideProductPriceRange: propsConfig.hideProductPriceRange,
            hideProductRating: propsConfig.hideProductRating,
            ctaType: propsConfig.ctaType,
            enableNotifyMe: propsConfig.enableNotifyMe,
            enableQuickView: propsConfig.enableQuickView,
            enableMobileQuickView: propsConfig.enableMobileQuickView,
            hideReadReviewsLink: propsConfig.productGridHideReadReviewsLink,
            hideReviewsNumber: propsConfig.hideReviewsNumber,
            enableShadePicker: propsConfig.enableShadePicker,
            enableSizePicker: propsConfig.productCarouselEnableSizePicker,
            enableHoverInteraction: propsConfig.enableHoverInteraction,
            shortDescriptionLocation: propsConfig.shortDescriptionLocation,
            enableProductDetailLink: propsConfig.enableProductDetailLink,
            enableQuickViewShortcut: propsConfig.enableQuickViewShortcut,
            enableAutoReplenishment: propsConfig.enableAutoReplenishment,
            autoReplenishOptions: propsConfig.autoReplenishOptions || [],
            autoReplenishToggleType: propsConfig.autoReplenishToggleType,
            showProductQuantitySelector: propsConfig.showProductQuantitySelector,
            hideProductPricePerUnit: propsConfig.hideProductPricePerUnit,
            translationsOverwrites: propsConfig.translationsOverwrites
        };

        const productGrid = (
            <>
                {this.renderGridHeader()}
                <ProductGrid
                    formatter={Formatter.CAROUSEL}
                    carouselProps={productCarouselProps}
                    briefProps={this.getBriefProps()}
                    positionGridInsideAccordion={
                        this.props.viewController.positionGridInsideAccordion
                    }
                    productGridCTAConfig={productGridCTAConfig}
                    enableFilters={false}
                />
            </>
        );

        return propsConfig.enableAccordion ? (
            <ProductAccordion
                iconType={propsConfig.gridAccordionIconType}
                accordionTitle={propsConfig.accordionTitle || ''}
                accordionSubtext={propsConfig.accordionSubtext || ''}
                accordionDefaultOpen={!!propsConfig.accordionDefaultOpen}
                onChange={this.setGridPosition}
            >
                {productGrid}
            </ProductAccordion>
        ) : (
            productGrid
        );
    };

    private getMainGridContent() {
        const { data, setProductSortingDropdownValue } = this.props.viewController;
        const { isAllGiftsFilterApplied, appliedFilters } = this.props.viewController.data;

        const {
            productsNumber,
            isFilterDisplayStacked,
            areFiltersApplied,
            productSortingDropdownValue
        } = data;
        const { gridCTA, hideSorting, filterData } = this.props.config;

        const productGridCTAConfig = {
            enabled: gridCTA && gridCTA.enabled,
            label: gridCTA && gridCTA.label,
            absoluteUrl: gridCTA && gridCTA.absoluteUrl,
            url: gridCTA && gridCTA.url,
            newTab: gridCTA && gridCTA.newTab
        };

        return (
            <StyledContainer>
                <ProductGrid
                    formatter={Formatter.DEFAULT}
                    briefProps={this.getBriefProps()}
                    isAllGiftsFilterApplied={isAllGiftsFilterApplied}
                    areFiltersApplied={areFiltersApplied}
                    productGridCTAConfig={productGridCTAConfig}
                    hideSorting={hideSorting}
                    productsNumber={productsNumber}
                    isFilterDisplayStacked={isFilterDisplayStacked}
                    isFilterDisplayTabbed={!isFilterDisplayStacked}
                    productSortingDropdownValue={productSortingDropdownValue}
                    setProductSortingDropdownValue={setProductSortingDropdownValue}
                    filterSets={this.props.viewController.filters}
                    enableFilters={this.props.config.enableFilters}
                    appliedFilters={appliedFilters}
                    totalAppliedFilters={this.props.viewController.totalAppliedFilters}
                    filtersDisplayType={filterData && filterData.filtersDisplayType}
                />
            </StyledContainer>
        );
    }

    private renderContainer() {
        const {
            gridAccordionIconType,
            accordionTitle = '',
            accordionSubtext = '',
            accordionDefaultOpen = false,
            enableAccordion
        } = this.props.config;

        const header = this.renderGridHeader();

        return (
            <>
                {header && <Container>{header}</Container>}
                {enableAccordion ? (
                    <ProductAccordion
                        iconType={gridAccordionIconType}
                        accordionTitle={accordionTitle}
                        accordionSubtext={accordionSubtext}
                        accordionDefaultOpen={accordionDefaultOpen}
                    >
                        {this.getMainGridContent()}
                    </ProductAccordion>
                ) : (
                    this.getMainGridContent()
                )}
            </>
        );
    }

    public render() {
        const { enableGridCarousel } = this.props.config;
        const isLoaded = this.props.viewController.data.isLoaded;

        if (!isLoaded) {
            return null;
        }

        if (enableGridCarousel) {
            return this.renderGridCarousel();
        }

        return (
            <>
                {this.renderContainer()}
                <CartLimitReachedModal />
            </>
        );
    }
}

ProductGridWrapper.displayName = 'ProductGrid';

export default ProductGridWrapper;
